"use client";

import { useEffect, useState } from "react";
import dynamic from "next/dynamic";

import { AccountElementLink } from "@/components/account-element-link";
import { SignInLink } from "@/components/sign-in-link";
import { usePermalinkPath } from "@/lib/permalinks-context";
import { useTranslator } from "@/lib/translation-context";

// Lazy boundary — identical rationale to `auth-menu.tsx`'s server-side
// `UserAvatarMenu` import: the chunk must load only for a signed-in visitor,
// never for the anonymous default this island renders on first paint.
// `next/dynamic` defers it until the BFF fetch below resolves `signedIn:true`.
const UserAvatarMenu = dynamic(() =>
  import("@/app/(account)/_components/user-avatar-menu").then(
    (m) => m.UserAvatarMenu,
  ),
);

interface MeSummary {
  signedIn: boolean;
  name?: string | null;
  firstName?: string | null;
  email?: string | null;
  initials?: string;
  avatarUrl?: string | null;
}

interface MeSummaryEnvelope {
  success: true;
  data: MeSummary;
}

function isSignedInEnvelope(value: unknown): value is MeSummaryEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { success?: unknown }).success === true &&
    Boolean((value as { data?: MeSummary }).data?.signedIn)
  );
}

/**
 * Client-hydrated replacement for `<AuthMenu>` on the `sites` tree
 * (storefront ISR refactor, Task 5A — see
 * `docs/superpowers/specs/2026-07-01-storefront-root-layout-restructure-design.md`).
 *
 * `<AuthMenu>` resolves signed-in state on the server via `cookies()` — a
 * Next dynamic API that would taint the whole `sites` layout away from static
 * ISR. This island instead renders the exact `SignedOutMenu` markup as its
 * static default (the render everyone, including crawlers, gets — no extra JS,
 * no CLS, no loading skeleton), then fetches `/api/me/summary` client-side and
 * swaps in the lazy `UserAvatarMenu` only when the BFF reports
 * `signedIn:true`. A signed-out response or a failed fetch leaves the "Log in"
 * link in place — the avatar never mounts for those visits.
 *
 * `(public)`'s `<AuthMenu>` is untouched; it keeps resolving server-side
 * there since that tree is dynamic anyway.
 */
export function AuthIsland({
  label,
}: {
  /** Set by an applied header's Account element — see `<AuthMenu>`. */
  label?: string;
}): React.ReactElement {
  const t = useTranslator();
  const signInHref = usePermalinkPath()("travelerLogin");
  const [summary, setSummary] = useState<MeSummary | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    // Trailing slash avoids the `trailingSlash: true` 308 round-trip.
    fetch("/api/me/summary/", { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (isSignedInEnvelope(body)) {
          setSummary(body.data);
        }
      })
      .catch(() => {
        // Aborted (unmount) or network error — stay on the static
        // signed-out default; never surface this to the header.
      });

    return () => controller.abort();
  }, []);

  if (summary?.signedIn) {
    return (
      <>
        <UserAvatarMenu
          initials={summary.initials ?? "T"}
          name={summary.firstName ?? null}
          email={summary.email ?? null}
          avatarUrl={summary.avatarUrl ?? null}
        />
      </>
    );
  }

  if (label !== undefined) {
    return (
      <AccountElementLink
        label={label}
        iconOnlyName={t("Log In")}
        href={signInHref}
      />
    );
  }
  return <SignInLink label={t("Log In")} href={signInHref} />;
}
